import {useEffect, useState} from "react";

import {WealthyEnv} from "helpers";

const useRecaptcha = (
  action = "homepage",
  refreshInterval = 1000 * 60 * 3,
  siteKey = WealthyEnv.RECAPTCHA_SITEKEY
) => {
  const [token, setToken] = useState(null);

  const getRecaptcha = () => {
    if (typeof window === "undefined") return null;
    return window.grecaptcha;
  };

  const waitForRecaptchaReady = (timeoutMs = 10000, pollMs = 300) => new Promise((resolve, reject) => {
    const start = Date.now();

    const check = () => {
      const grecaptcha = getRecaptcha();
      if (grecaptcha && typeof grecaptcha.ready === "function") {
        grecaptcha.ready(() => resolve(grecaptcha));
        return;
      }

      if (Date.now() - start >= timeoutMs) {
        reject(new Error("recaptcha_not_ready"));
        return;
      }

      setTimeout(check, pollMs);
    };

    check();
  });

  const initCaptcha = async () => {
    try {
      const grecaptcha = await waitForRecaptchaReady();
      const newToken = await grecaptcha.execute(siteKey, {action});
      setToken(newToken);
      return newToken;
    } catch {
      setToken(null);
      return null;
    }
  };

  useEffect(() => {
    let isCancelled = false;

    const initWithRetry = async (attempt = 0) => {
      if (isCancelled) return;
      const newToken = await initCaptcha();
      if (!newToken && attempt < 5) {
        const retryDelay = Math.min(1000 * (attempt + 1), 3000);
        setTimeout(() => {
          initWithRetry(attempt + 1);
        }, retryDelay);
      }
    };

    // Initial captcha initialization
    initWithRetry();

    // Set up interval for token refresh
    const interval = setInterval(() => {
      initWithRetry();
    }, refreshInterval);

    // Cleanup interval on unmount
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [action, refreshInterval, siteKey]);

  return {
    token,
    refreshToken: initCaptcha
  };
};

export default useRecaptcha;
